import {useLocation, useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import './SorryPage.css'

interface SorryLocationState {
  title?: string
  description?: string
  error?: string
  variant?: SorryVariant
  /** Path to return to when the user clicks Reload. */
  returnTo?: string
}

type SorryVariant = 'fatal' | 'notFound'

interface SorryPageProps {
  variant?: SorryVariant
  error?: Error | string
}

const SorryPage = ({variant: variantProp, error: errorProp}: SorryPageProps) => {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const state = (location.state ?? {}) as SorryLocationState
  const variant: SorryVariant = variantProp ?? state.variant ?? 'fatal'

  const isNotFound = variant === 'notFound'

  const title = state.title ?? (isNotFound ? t('sorry.notFound.title') : t('sorry.title'))
  const description = state.description ?? (isNotFound ? t('sorry.notFound.description') : t('sorry.description'))

  const errorText = errorProp
    ? errorProp instanceof Error
      ? (errorProp.stack ?? errorProp.message)
      : errorProp
    : state.error
  const showDetails = !isNotFound && errorText && import.meta.env.DEV

  // Full-page reload to the originating route (or home) so the user lands somewhere
  // usable when the underlying issue resolves. window.location.assign clears in-memory
  // state (useMe cache, escalation flags) that would otherwise re-trigger the escalation
  // immediately on SPA navigation.
  const handleReload = () => {
    const target = state.returnTo && state.returnTo !== '/error' ? state.returnTo : '/'
    window.location.assign(target)
  }
  const handleGoHome = () => navigate('/', {replace: true})

  return (
    <main className="sorry-page">
      <div className="sorry-card">
        <div className="sorry-emoji" aria-hidden>
          {isNotFound ? '🧭' : '😵‍💫'}
        </div>
        <h1 className="sorry-title">{title}</h1>
        <p className="sorry-description">{description}</p>
        <div className="sorry-actions">
          {!isNotFound && (
            <button type="button" onClick={handleReload} className="sorry-btn sorry-btn-primary">
              {t('sorry.reload')}
            </button>
          )}
          <button
            type="button"
            onClick={handleGoHome}
            className={`sorry-btn ${isNotFound ? 'sorry-btn-primary' : 'sorry-btn-secondary'}`}
          >
            {t('sorry.goHome')}
          </button>
        </div>
        {showDetails && (
          <details className="sorry-details">
            <summary>Details (dev only)</summary>
            <pre>{errorText}</pre>
          </details>
        )}
      </div>
    </main>
  )
}

export default SorryPage
